import React, { useEffect, useMemo, useState } from "react";
import { Mafs, Plot, Point, Coordinates } from "mafs";
import { Slider, Card, Typography, Row, Col, Divider } from "antd";

const { Text } = Typography;

const DEFAULT_POINTS = [
    [1, 1],
    [3, 3],
];

// Mirrors FunctionMatchValidator clamp: [0.05, 0.5]
function clampTolerance(t) {
    const n = Number(t);
    const safe = Number.isFinite(n) ? n : 0.2;
    return Math.max(0.05, Math.min(safe, 0.5));
}

function normalizeFamily(family) {
    const f = (family || "LINEAR").toString().trim().toUpperCase();
    if (f === "QUADRATIC" || f === "EXPONENTIAL" || f === "LINEAR") return f;
    return "LINEAR";
}

function defaultParamsFor(family) {
    switch (family) {
        case "QUADRATIC":
            return { a: 1, b: 0, c: 0 };
        case "EXPONENTIAL":
            // Using y = a * e^(b x)
            return { a: 1, b: 0.5 };
        case "LINEAR":
        default:
            return { m: 1, c: 0 };
    }
}

export const FunctionMatcher = ({ config }) => {
    const safeConfig = config ?? {};
    const family = normalizeFamily(safeConfig.family);
    const tolerance = clampTolerance(safeConfig.tolerance);

    const goalInstructions =
        typeof safeConfig.goalInstructions === "string" && safeConfig.goalInstructions.trim()
            ? safeConfig.goalInstructions.trim()
            : "Adjust the parameters to fit the function through all target points.";

    const targetPoints = useMemo(() => {
        const pts = Array.isArray(safeConfig.targetPoints) ? safeConfig.targetPoints : [];
        const normalized = pts
            .map((p) => (Array.isArray(p) && p.length >= 2 ? [Number(p[0]), Number(p[1])] : null))
            .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
        return normalized.length > 0 ? normalized : DEFAULT_POINTS;
    }, [safeConfig.targetPoints]);

    const [params, setParams] = useState(() => defaultParamsFor(family));

    // If family changes, reset parameters to sensible defaults
    useEffect(() => {
        setParams(defaultParamsFor(family));
    }, [family]);

    const f = (x) => {
        switch (family) {
            case "QUADRATIC": {
                const { a, b, c } = params;
                return a * x * x + b * x + c;
            }
            case "EXPONENTIAL": {
                const { a, b } = params;
                return a * Math.exp(b * x);
            }
            case "LINEAR":
            default: {
                const { m, c } = params;
                return m * x + c;
            }
        }
    };

    const isHit = (px, py) => Math.abs(py - f(px)) <= tolerance;
    const allHit = targetPoints.every(([x, y]) => isHit(x, y));

    // Auto viewBox based on target points (simple + safe)
    const viewBox = useMemo(() => {
        const xs = targetPoints.map((p) => p[0]);
        const ys = targetPoints.map((p) => p[1]);

        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const padX = Math.max(2, (maxX - minX) * 0.25);
        const padY = Math.max(2, (maxY - minY) * 0.25);

        return {
            x: [minX - padX, maxX + padX],
            y: [minY - padY, maxY + padY],
        };
    }, [targetPoints]);

    const successBorder = allHit ? { borderColor: "#10b981" } : undefined;

    return (
        <Card title="Function Matcher" style={successBorder}>
            <Text>{goalInstructions}</Text>
            <Divider style={{ margin: "12px 0" }} />

            <Row gutter={[16, 16]}>
                <Col span={16}>
                    <Mafs viewBox={viewBox}>
                        <Coordinates.Cartesian />
                        <Plot.OfX y={(x) => f(x)} />
                        {targetPoints.map(([x, y], i) => (
                            <Point key={i} x={x} y={y} color={isHit(x, y) ? "#10b981" : "#ef4444"} />
                        ))}
                    </Mafs>
                </Col>

                <Col span={8}>
                    {family === "LINEAR" && (
                        <>
                            <Text>Adjust slope (m): {params.m.toFixed(2)}</Text>
                            <Slider
                                min={-5}
                                max={5}
                                step={0.1}
                                value={params.m}
                                onChange={(v) => setParams((p) => ({ ...p, m: v }))}
                            />

                            <Text>Adjust intercept (c): {params.c.toFixed(2)}</Text>
                            <Slider
                                min={-10}
                                max={10}
                                step={0.1}
                                value={params.c}
                                onChange={(v) => setParams((p) => ({ ...p, c: v }))}
                            />
                        </>
                    )}

                    {family === "QUADRATIC" && (
                        <>
                            <Text>Adjust a: {params.a.toFixed(2)}</Text>
                            <Slider
                                min={-2}
                                max={2}
                                step={0.05}
                                value={params.a}
                                onChange={(v) => setParams((p) => ({ ...p, a: v }))}
                            />

                            <Text>Adjust b: {params.b.toFixed(2)}</Text>
                            <Slider
                                min={-5}
                                max={5}
                                step={0.1}
                                value={params.b}
                                onChange={(v) => setParams((p) => ({ ...p, b: v }))}
                            />

                            <Text>Adjust c: {params.c.toFixed(2)}</Text>
                            <Slider
                                min={-10}
                                max={10}
                                step={0.1}
                                value={params.c}
                                onChange={(v) => setParams((p) => ({ ...p, c: v }))}
                            />
                        </>
                    )}

                    {family === "EXPONENTIAL" && (
                        <>
                            <Text>Adjust a: {params.a.toFixed(2)}</Text>
                            <Slider
                                min={-5}
                                max={5}
                                step={0.1}
                                value={params.a}
                                onChange={(v) => setParams((p) => ({ ...p, a: v }))}
                            />

                            <Text>Adjust b: {params.b.toFixed(2)}</Text>
                            <Slider
                                min={-2}
                                max={2}
                                step={0.05}
                                value={params.b}
                                onChange={(v) => setParams((p) => ({ ...p, b: v }))}
                            />
                        </>
                    )}

                    <Divider style={{ margin: "12px 0" }} />
                    <Text>
                        Tolerance: <strong>{tolerance.toFixed(2)}</strong>
                    </Text>
                    <br />
                    {allHit && (
                        <Text type="success" strong>
                            Success! All points matched.
                        </Text>
                    )}
                </Col>
            </Row>
        </Card>
    );
};
